import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { TriangleAlertIcon } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { useAuth } from '@/context/AuthContext';
import { useFetch } from '@/hooks/useFecth';
import server from '@/services/server';
import useClubErrors from '../hooks/useClubErrors';
import { useClubConfirm } from '../providers/ClubConfirmProvider';

const roleLabels = {
    owner: 'Proprietário',
    admin: 'Administrador',
    manager: 'Gerente',
    user: 'Usuário',
};

const emailSchema = z.object({
    email: z.string().email('Informe um e-mail válido'),
    currentPassword: z.string().min(1, 'Informe a senha atual'),
});

const passwordSchema = z
    .object({
        currentPassword: z.string().min(1, 'Informe a senha atual'),
        password: z.string().min(8, 'Mínimo de 8 caracteres'),
        confirmPassword: z.string().min(1, 'Confirme a nova senha'),
    })
    .refine(data => data.password === data.confirmPassword, {
        message: 'As senhas devem ser iguais',
        path: ['confirmPassword'],
    });

const deleteSchema = z.object({
    currentPassword: z.string().min(1, 'Informe a senha atual'),
    confirmText: z
        .string()
        .refine(value => value.trim().toUpperCase() === 'EXCLUIR', {
            message: 'Digite EXCLUIR para confirmar',
        }),
});

export default function ClubProfile() {
    const navigate = useNavigate();
    const { user, LogOut } = useAuth();
    const confirm = useClubConfirm();
    const { setErrors, enqueueSnackbar } = useClubErrors();
    const { data: profile, mutate, isFetching } = useFetch('club/me');
    const [deleting, setDeleting] = useState(false);

    const emailForm = useForm({
        resolver: zodResolver(emailSchema),
        defaultValues: { email: '', currentPassword: '' },
    });
    const passwordForm = useForm({
        resolver: zodResolver(passwordSchema),
        defaultValues: { currentPassword: '', password: '', confirmPassword: '' },
    });
    const deleteForm = useForm({
        resolver: zodResolver(deleteSchema),
        defaultValues: { currentPassword: '', confirmText: '' },
    });
    const deletePassword = deleteForm.watch('currentPassword');
    const deleteConfirmText = deleteForm.watch('confirmText');

    useEffect(() => {
        if (profile?.userName || profile?.email) {
            emailForm.reset({
                email: profile.userName || profile.email || '',
                currentPassword: '',
            });
        }
    }, [profile, emailForm]);

    const displayEmail = profile?.userName || profile?.email || user?.userName || '';
    const role = profile?.role || user?.role;
    const isOwner = role === 'owner';
    const canDeleteAccount =
        !isOwner &&
        Boolean(String(deletePassword || '').trim()) &&
        String(deleteConfirmText || '').trim().toUpperCase() === 'EXCLUIR';

    const onUpdateEmail = async values => {
        try {
            const { data } = await server.put('club/me', {
                email: values.email.trim().toLowerCase(),
                currentPassword: values.currentPassword,
            });
            const payload = data?.data || data;
            enqueueSnackbar('E-mail atualizado. Entre novamente com o novo e-mail.');
            if (payload?.emailChanged) {
                LogOut();
                navigate('/clube/login', { replace: true });
                return;
            }
            await mutate();
            emailForm.setValue('currentPassword', '');
        } catch (error) {
            setErrors(error);
        }
    };

    const onUpdatePassword = async values => {
        try {
            await server.put('club/me', {
                password: values.password,
                currentPassword: values.currentPassword,
            });
            enqueueSnackbar('Senha atualizada.');
            passwordForm.reset({ currentPassword: '', password: '', confirmPassword: '' });
        } catch (error) {
            setErrors(error);
        }
    };

    const onDeleteAccount = async values => {
        try {
            await confirm({
                title: 'Excluir conta',
                description:
                    'Esta ação remove seu acesso a todos os clubes. Não poderá ser desfeita pelo painel.',
                confirmationText: 'Excluir conta',
                confirmationButtonProps: { variant: 'destructive' },
            });
        } catch {
            return;
        }

        try {
            setDeleting(true);
            await server.delete('club/me', {
                data: { currentPassword: values.currentPassword },
            });
            enqueueSnackbar('Conta excluída.');
            LogOut();
            navigate('/clube/login', { replace: true });
        } catch (error) {
            setErrors(error);
            setDeleting(false);
        }
    };

    return (
        <div className="flex flex-col gap-6">
            <div>
                <h1 className="text-2xl font-bold tracking-tight">Perfil</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                    Gerencie o e-mail de login, a senha e a exclusão da sua conta de usuário.
                </p>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Dados da conta</CardTitle>
                    <CardDescription>Informações do usuário logado neste painel.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                    {isFetching && !profile ? (
                        <div className="flex items-center gap-2 text-muted-foreground">
                            <Spinner className="size-4" />
                            Carregando…
                        </div>
                    ) : (
                        <>
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="text-muted-foreground">E-mail / login:</span>
                                <span className="font-medium">{displayEmail || '—'}</span>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="text-muted-foreground">Cargo neste clube:</span>
                                <Badge variant="outline">{roleLabels[role] || role || '—'}</Badge>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Alterar e-mail</CardTitle>
                    <CardDescription>
                        O e-mail é o seu login no painel. Após trocar, será necessário entrar de novo.
                    </CardDescription>
                </CardHeader>
                <form onSubmit={emailForm.handleSubmit(onUpdateEmail)} className="contents">
                    <CardContent>
                        <FieldGroup>
                            <Field data-invalid={Boolean(emailForm.formState.errors.email)}>
                                <FieldLabel>Novo e-mail</FieldLabel>
                                <Controller
                                    name="email"
                                    control={emailForm.control}
                                    render={({ field }) => (
                                        <Input
                                            type="email"
                                            autoComplete="email"
                                            {...field}
                                            value={field.value ?? ''}
                                        />
                                    )}
                                />
                                <FieldError>{emailForm.formState.errors.email?.message}</FieldError>
                            </Field>
                            <Field data-invalid={Boolean(emailForm.formState.errors.currentPassword)}>
                                <FieldLabel>Senha atual</FieldLabel>
                                <Controller
                                    name="currentPassword"
                                    control={emailForm.control}
                                    render={({ field }) => (
                                        <Input
                                            type="password"
                                            autoComplete="current-password"
                                            {...field}
                                            value={field.value ?? ''}
                                        />
                                    )}
                                />
                                <FieldError>
                                    {emailForm.formState.errors.currentPassword?.message}
                                </FieldError>
                            </Field>
                        </FieldGroup>
                    </CardContent>
                    <CardFooter className="justify-end">
                        <Button type="submit" disabled={emailForm.formState.isSubmitting}>
                            {emailForm.formState.isSubmitting ? <Spinner className="size-4" /> : null}
                            Salvar e-mail
                        </Button>
                    </CardFooter>
                </form>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Alterar senha</CardTitle>
                    <CardDescription>Use uma senha forte. Você permanece logado após a troca.</CardDescription>
                </CardHeader>
                <form onSubmit={passwordForm.handleSubmit(onUpdatePassword)} className="contents">
                    <CardContent>
                        <FieldGroup>
                            <Field data-invalid={Boolean(passwordForm.formState.errors.currentPassword)}>
                                <FieldLabel>Senha atual</FieldLabel>
                                <Controller
                                    name="currentPassword"
                                    control={passwordForm.control}
                                    render={({ field }) => (
                                        <Input
                                            type="password"
                                            autoComplete="current-password"
                                            {...field}
                                            value={field.value ?? ''}
                                        />
                                    )}
                                />
                                <FieldError>
                                    {passwordForm.formState.errors.currentPassword?.message}
                                </FieldError>
                            </Field>
                            <Field data-invalid={Boolean(passwordForm.formState.errors.password)}>
                                <FieldLabel>Nova senha</FieldLabel>
                                <Controller
                                    name="password"
                                    control={passwordForm.control}
                                    render={({ field }) => (
                                        <Input
                                            type="password"
                                            autoComplete="new-password"
                                            {...field}
                                            value={field.value ?? ''}
                                        />
                                    )}
                                />
                                <FieldError>{passwordForm.formState.errors.password?.message}</FieldError>
                            </Field>
                            <Field data-invalid={Boolean(passwordForm.formState.errors.confirmPassword)}>
                                <FieldLabel>Confirmar nova senha</FieldLabel>
                                <Controller
                                    name="confirmPassword"
                                    control={passwordForm.control}
                                    render={({ field }) => (
                                        <Input
                                            type="password"
                                            autoComplete="new-password"
                                            {...field}
                                            value={field.value ?? ''}
                                        />
                                    )}
                                />
                                <FieldError>
                                    {passwordForm.formState.errors.confirmPassword?.message}
                                </FieldError>
                            </Field>
                        </FieldGroup>
                    </CardContent>
                    <CardFooter className="justify-end">
                        <Button type="submit" disabled={passwordForm.formState.isSubmitting}>
                            {passwordForm.formState.isSubmitting ? <Spinner className="size-4" /> : null}
                            Atualizar senha
                        </Button>
                    </CardFooter>
                </form>
            </Card>

            <Card className="border-destructive/30">
                <CardHeader>
                    <CardTitle className="text-base text-destructive">Excluir conta</CardTitle>
                    <CardDescription>
                        Remove permanentemente o acesso desta conta de usuário ao painel.
                    </CardDescription>
                </CardHeader>
                <form onSubmit={deleteForm.handleSubmit(onDeleteAccount)} className="contents">
                    <CardContent className="space-y-4">
                        {isOwner ? (
                            <Alert variant="error">
                                <TriangleAlertIcon />
                                <AlertTitle>Você é proprietário deste clube</AlertTitle>
                                <AlertDescription>
                                    Antes de excluir a conta, transfira a propriedade para outro membro. Enquanto
                                    for owner, a exclusão será bloqueada.
                                </AlertDescription>
                            </Alert>
                        ) : null}

                        <FieldGroup>
                            <Field data-invalid={Boolean(deleteForm.formState.errors.currentPassword)}>
                                <FieldLabel>Senha atual</FieldLabel>
                                <Controller
                                    name="currentPassword"
                                    control={deleteForm.control}
                                    render={({ field }) => (
                                        <Input
                                            type="password"
                                            autoComplete="current-password"
                                            {...field}
                                            value={field.value ?? ''}
                                        />
                                    )}
                                />
                                <FieldError>
                                    {deleteForm.formState.errors.currentPassword?.message}
                                </FieldError>
                            </Field>
                            <Field data-invalid={Boolean(deleteForm.formState.errors.confirmText)}>
                                <FieldLabel>Digite EXCLUIR para confirmar</FieldLabel>
                                <Controller
                                    name="confirmText"
                                    control={deleteForm.control}
                                    render={({ field }) => (
                                        <Input {...field} value={field.value ?? ''} />
                                    )}
                                />
                                <FieldError>{deleteForm.formState.errors.confirmText?.message}</FieldError>
                            </Field>
                        </FieldGroup>
                    </CardContent>
                    <CardFooter className="justify-end">
                        <Button
                            type="submit"
                            variant="destructive"
                            disabled={!canDeleteAccount || deleting || deleteForm.formState.isSubmitting}
                        >
                            {deleting || deleteForm.formState.isSubmitting ? (
                                <Spinner className="size-4" />
                            ) : null}
                            Excluir minha conta
                        </Button>
                    </CardFooter>
                </form>
            </Card>
        </div>
    );
}
